import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useRef, useState } from 'react';
import { cn } from '@/lib/utils.ts';
import { LuEyeOff, LuHighlighter } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';

type ToolMode = 'highlight' | 'hide';

interface Rect {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  type: ToolMode;
}

interface ScreenshotEditorDialogProps {
  screenshot: string;
  open: boolean;
  onClose: () => void;
  onSave: (editedBlob: Blob) => void;
}

export default function ScreenshotEditorDialog({ screenshot, open, onClose, onSave }: ScreenshotEditorDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ToolMode>('highlight');
  const [rects, setRects] = useState<Rect[]>([]);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [tempRect, setTempRect] = useState<Rect | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const rectIdCounter = useRef(0);

  function getPos(e: MouseEvent): { x: number; y: number };
  function getPos(e: Touch): { x: number; y: number };
  function getPos(e: MouseEvent | Touch): { x: number; y: number } {
    const wrapper = wrapperRef.current!;
    const bounds = wrapper.getBoundingClientRect();
    const clientX = 'clientX' in e ? e.clientX : 0;
    const clientY = 'clientY' in e ? e.clientY : 0;
    const offsetX = clientX - bounds.left;
    const offsetY = clientY - bounds.top;
    const xPercent = offsetX / bounds.width;
    const yPercent = offsetY / bounds.height;
    return { x: xPercent, y: yPercent };
  }

  const createRect = (start: { x: number; y: number }, end: { x: number; y: number }): Rect => {
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(start.x - end.x);
    const h = Math.abs(start.y - end.y);
    return { id: rectIdCounter.current++, x, y, w, h, type: mode };
  };

  const beginDraw = (point: { x: number; y: number }) => {
    if (startPos) return;
    setStartPos(point);
    setTempRect(null);
  };

  const updateDraw = (point: { x: number; y: number }) => {
    if (!startPos) return;
    setTempRect(createRect(startPos, point));
  };

  const finishDraw = (point: { x: number; y: number }) => {
    if (!startPos) return;
    const rect = createRect(startPos, point);
    if (rect.w > 0.01 && rect.h > 0.01) setRects((prev) => [...prev, rect]);
    setStartPos(null);
    setTempRect(null);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => beginDraw(getPos(e.nativeEvent));
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => updateDraw(getPos(e.nativeEvent));
  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => finishDraw(getPos(e.nativeEvent));
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => beginDraw(getPos(e.nativeEvent.touches[0]));
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => updateDraw(getPos(e.nativeEvent.touches[0]));
  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const p = e.nativeEvent.changedTouches[0];
    if (p) finishDraw(getPos(p));
  };

  const handleSave = () => {
    const image = imageRef.current;
    if (!image) return;

    const naturalW = image.naturalWidth;
    const naturalH = image.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = naturalW;
    canvas.height = naturalH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    for (const rect of rects) {
      const x = rect.x * naturalW;
      const y = rect.y * naturalH;
      const w = rect.w * naturalW;
      const h = rect.h * naturalH;

      if (rect.type === 'highlight') {
        ctx.fillStyle = 'rgba(254, 240, 138, 0.2)';
        ctx.fillRect(x, y, w, h);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(250, 204, 21, 1)';
        ctx.strokeRect(x, y, w, h);
      } else {
        ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        ctx.fillRect(x, y, w, h);
      }
    }

    canvas.toBlob((blob) => blob && onSave(blob), 'image/jpeg', 0.85);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()} modal>
      <DialogContent className="max-h-[90%] min-w-[90%] flex flex-col items-center overflow-hidden">
        <DialogHeader className="w-full items-start">
          <DialogTitle className="text-left">{t('feedback.editorTitle')}</DialogTitle>
        </DialogHeader>

        <div
          ref={wrapperRef}
          className="relative flex overflow-hidden select-none touch-none bg-gray-100 border border-gray-300 rounded-md shadow-inner p-2"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img ref={imageRef} src={screenshot} alt="Screenshot" draggable={false} />

          {rects.map((r) => (
            <div
              key={r.id}
              className={cn(
                'absolute border-2 transition',
                r.type === 'highlight'
                  ? 'bg-yellow-100/20 border-yellow-400 hover:bg-yellow-100/40'
                  : 'bg-black/100 hover:bg-black/80',
                selectedId === r.id && 'ring-2 ring-indigo-500'
              )}
              style={{
                left: `${r.x * 100}%`,
                top: `${r.y * 100}%`,
                width: `${r.w * 100}%`,
                height: `${r.h * 100}%`,
              }}
              onClick={() => setSelectedId(r.id)}
            >
              {selectedId === r.id && (
                <button
                  onClick={() => setRects((prev) => prev.filter((rect) => rect.id !== r.id))}
                  className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white text-gray-800 text-sm rounded-full border border-gray-300 flex items-center justify-center shadow-sm hover:bg-gray-100"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          {tempRect && (
            <div
              className={cn(
                'absolute pointer-events-none border-2 z-10',
                tempRect.type === 'highlight' ? 'bg-yellow-100/20 border-yellow-400' : 'bg-black border-transparent'
              )}
              style={{
                left: `${tempRect.x * 100}%`,
                top: `${tempRect.y * 100}%`,
                width: `${tempRect.w * 100}%`,
                height: `${tempRect.h * 100}%`,
              }}
            />
          )}
        </div>

        <DialogFooter className="w-full flex flex-col gap-4">
          <div className="flex flex-row gap-2 justify-center">
            <button
              onClick={() => setMode('highlight')}
              className={cn(
                'flex-1 sm:flex-initial inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-medium border transition',
                mode === 'highlight'
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-500'
                  : 'bg-white text-gray-900 border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              )}
            >
              <LuHighlighter className="w-4 h-4 mr-1" />
              {t('feedback.highlight')}
            </button>
            <button
              onClick={() => setMode('hide')}
              className={cn(
                'flex-1 sm:flex-initial inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-medium border transition',
                mode === 'hide'
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-500'
                  : 'bg-white text-gray-900 border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              )}
            >
              <LuEyeOff className="w-4 h-4 mr-1" />
              {t('feedback.hide')}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:justify-end">
            <button
              onClick={onClose}
              className="w-full sm:w-auto inline-flex justify-center rounded-full border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-800 hover:border-gray-400 hover:bg-gray-50 transition"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="w-full sm:w-auto inline-flex justify-center rounded-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 px-6 py-2 text-sm font-medium text-white shadow transition"
            >
              {t('common.done')}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
